import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { dateTime, eur, fullName, usd } from '../../services/format';
import { OrgPicker } from '../../components/OrgPicker';
import { Alert, Badge, Button, Card, Cell, Checkbox, Confirm, Field, Input, KeyValue, PageTitle, Row, Select, Table } from '../../components/ui';
import type { OrganizationAdmin, UserAdmin } from '../../types/api';

export function UserDetail() {
  const { id } = useParams();
  const userId = Number(id);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: user, error, reload } = useAsync(() => api.user(userId), [userId]);
  const audit = useAsync(() => api.audit({ target_kind: 'user', target_id: String(userId), page_size: 30 }), [userId]);
  const [notice, setNotice] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [move, setMove] = useState(false);
  const [moveOrg, setMoveOrg] = useState<OrganizationAdmin | null>(null);
  const [moveRole, setMoveRole] = useState('member');
  const [del, setDel] = useState(false);
  const [objecting, setObjecting] = useState(false);

  const run = async (name: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(name);
    setFail(null);
    setNotice(null);
    try {
      await fn();
      if (done) setNotice(done);
      reload();
      audit.reload();
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  if (error) return <Alert>{error}</Alert>;
  if (!user) return <p className="text-faint">{t('common.loading')}</p>;

  return (
    <div>
      <PageTitle
        title={fullName(user.first_name, user.last_name, user.email)}
        subtitle={<span className="font-mono">{user.email} · #{user.id}</span>}
        actions={
          <>
            <Button onClick={() => void run('reset', () => api.sendReset(user.id), t('users.resetSent'))} busy={busy === 'reset'}>
              {t('users.sendResetAction')}
            </Button>
            <Button onClick={() => setMove(true)}>{t('users.moveAction')}</Button>
            <Button variant="danger" onClick={() => setDel(true)}>
              {t('users.deleteAction')}
            </Button>
          </>
        }
      />
      {notice && <div className="mb-3"><Alert tone="good">{notice}</Alert></div>}
      {fail && <div className="mb-3"><Alert>{fail}</Alert></div>}
      <div className="grid gap-4 lg:grid-cols-2">
        <EditForm user={user} busy={busy === 'save'} onSave={(patch) => run('save', () => api.patchUser(user.id, patch), t('common.saved'))} />
        <div className="flex flex-col gap-4">
          <Card title={t('users.organization')}>
            {user.organization ? (
              <KeyValue
                rows={[
                  [t('users.organization'), <Link key="o" to={`/organizations/${user.organization.id}`} className="text-accent underline">{user.organization.name}</Link>],
                  [t('orgs.roleLabel'), t(`orgs.role.${user.organization.role}`)],
                  [t('orgs.kindLabel'), t(`orgs.kind.${user.organization.kind}`)],
                  [t('orgs.statusLabel'), <Badge key="s" tone={user.organization.status === 'active' ? 'good' : 'warn'}>{t(`orgs.status.${user.organization.status}`)}</Badge>],
                  [t('orgs.plan'), user.organization.plan_name],
                ]}
              />
            ) : (
              <span className="text-faint">{t('users.noOrganization')}</span>
            )}
          </Card>
          <Card title={t('users.usage')}>
            <KeyValue rows={[[t('users.spend'), eur(user.billed_eur, i18n.language)], [t('users.cost'), usd(user.cost_usd, i18n.language)], [t('users.requests'), user.request_count], [t('users.lastConnection'), dateTime(user.last_connection, i18n.language)], [t('users.createdAt'), dateTime(user.created_at, i18n.language)], [t('users.discovery'), user.discovery_source ? t(`users.discoverySource.${user.discovery_source}`) : '—'], [t('users.password'), user.sso_only ? t('users.ssoOnly') : t('users.hasPassword')]]} />
          </Card>
          <Card
            title={t('users.review.title')}
            actions={
              user.review_sample_objection_at ? (
                <Button busy={busy === 'objection'} onClick={() => void run('objection', () => api.setReviewObjection(user.id, false), t('users.review.withdrawn'))}>
                  {t('users.review.withdraw')}
                </Button>
              ) : (
                <Button onClick={() => setObjecting(true)}>{t('users.review.record')}</Button>
              )
            }
          >
            <div className="flex flex-col gap-1.5 text-[12.5px]" data-testid="review-objection">
              <p className="text-ink">
                {user.review_sample_objection_at ? t('users.review.objectedSince', { date: dateTime(user.review_sample_objection_at, i18n.language) }) : t('users.review.none')}
              </p>
              {user.organization?.review_sample_opt_out_at && (
                <p className="text-muted">{t('users.review.organizationOut', { date: dateTime(user.organization.review_sample_opt_out_at, i18n.language) })}</p>
              )}
              <p className="text-[12px] text-faint">{t('users.review.hint')}</p>
            </div>
          </Card>
          <Card title={t('users.identities')}>
            {user.identities.length === 0 ? (
              <span className="text-faint">{t('users.noIdentities')}</span>
            ) : (
              <KeyValue rows={user.identities.map((i) => [i.provider, <span key={i.provider} className="font-mono text-[11.5px]">{i.email ?? '—'} · {i.tenant_id ?? '—'} · {dateTime(i.last_used_at, i18n.language)}</span>])} />
            )}
          </Card>
        </div>
      </div>
      <h2 className="mb-2 mt-6 text-[13px] font-medium text-ink">{t('users.auditTitle')}</h2>
      <Table head={[t('audit.when'), t('audit.action'), t('audit.actor'), t('audit.data')]} empty={t('common.empty')}>
        {(audit.data?.items ?? []).map((e) => (
          <Row key={e.id}>
            <Cell mono>{dateTime(e.created_at, i18n.language)}</Cell>
            <Cell mono>{e.action}</Cell>
            <Cell mono>{e.actor_id ?? '—'}</Cell>
            <Cell mono className="max-w-md truncate" title={JSON.stringify(e.data)}>{JSON.stringify(e.data)}</Cell>
          </Row>
        ))}
      </Table>

      {move && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-ink/30 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-md border border-line-card bg-white p-5 shadow-lg">
            <h2 className="text-[15px] font-medium text-ink">{t('users.moveTitle')}</h2>
            <p className="mt-1 text-[12.5px] text-muted">{t('users.moveHint')}</p>
            <div className="mt-3 flex flex-col gap-3">
              <Field label={t('users.organization')}>
                <OrgPicker value={moveOrg} onChange={setMoveOrg} />
              </Field>
              <Field label={t('orgs.roleLabel')} className="w-40">
                <Select value={moveRole} onChange={(e) => setMoveRole(e.target.value)}>
                  <option value="member">{t('orgs.role.member')}</option>
                  <option value="admin">{t('orgs.role.admin')}</option>
                  <option value="owner">{t('orgs.role.owner')}</option>
                </Select>
              </Field>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button onClick={() => setMove(false)}>{t('common.cancel')}</Button>
              <Button variant="primary" disabled={!moveOrg} busy={busy === 'move'} onClick={() => moveOrg && void run('move', () => api.moveUser(user.id, moveOrg.id, moveRole), t('users.moved')).then(() => setMove(false))}>
                {t('users.moveConfirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
      <Confirm open={objecting} title={t('users.review.recordTitle')} message={t('users.review.recordMessage', { email: user.email })} confirmLabel={t('users.review.record')} busy={busy === 'objection'} onCancel={() => setObjecting(false)} onConfirm={() => void run('objection', () => api.setReviewObjection(user.id, true), t('users.review.recorded')).then(() => setObjecting(false))} />
      <Confirm open={del} danger title={t('users.deleteTitle')} message={t('users.deleteMessage', { email: user.email })} confirmLabel={t('users.deleteConfirm')} typed={user.email} busy={busy === 'delete'} onCancel={() => setDel(false)} onConfirm={() => void run('delete', () => api.deleteUser(user.id)).then(() => navigate('/users', { replace: true }))} />
    </div>
  );
}

function EditForm({ user, busy, onSave }: { user: UserAdmin; busy: boolean; onSave: (patch: { first_name: string; last_name: string; locale: string; is_active: boolean; is_staff: boolean; email_verified: boolean }) => void }) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(user.first_name);
  const [lastName, setLastName] = useState(user.last_name);
  const [locale, setLocale] = useState(user.locale);
  const [active, setActive] = useState(user.is_active);
  const [staff, setStaff] = useState(user.is_staff);
  const [verified, setVerified] = useState(user.email_verified);
  useEffect(() => {
    setFirstName(user.first_name);
    setLastName(user.last_name);
    setLocale(user.locale);
    setActive(user.is_active);
    setStaff(user.is_staff);
    setVerified(user.email_verified);
  }, [user]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave({ first_name: firstName, last_name: lastName, locale, is_active: active, is_staff: staff, email_verified: verified });
  };
  return (
    <form onSubmit={submit}>
      <Card title={t('users.account')} actions={<Button type="submit" variant="primary" busy={busy}>{t('common.save')}</Button>}>
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('users.firstName')}>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </Field>
            <Field label={t('users.lastName')}>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </Field>
          </div>
          <Field label={t('users.locale')} className="w-40">
            <Select value={locale} onChange={(e) => setLocale(e.target.value)}>
              <option value="en">English</option>
              <option value="fr">Français</option>
            </Select>
          </Field>
          <Checkbox label={t('users.activeHint')} checked={active} onChange={(e) => setActive(e.target.checked)} />
          <Checkbox label={t('users.staffHint')} checked={staff} onChange={(e) => setStaff(e.target.checked)} />
          <Checkbox label={t('users.verifiedHint')} checked={verified} onChange={(e) => setVerified(e.target.checked)} />
        </div>
      </Card>
    </form>
  );
}
